import { fetchAuthSession } from 'aws-amplify/auth';
import toast from 'react-hot-toast';
import awsconfig from '../aws-exports';

const API_ENDPOINT = awsconfig?.aws_cloud_logic_custom?.[0]?.endpoint || '';

async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    const idToken = session?.tokens?.idToken?.toString();
    return idToken ? { Authorization: idToken } : {};
  } catch {
    return {};
  }
}

async function request(path, { method = 'GET', body = null, params = null } = {}) {
  const authHeaders = await getAuthHeaders();
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json', ...authHeaders };

  let url = `${API_ENDPOINT}${path}`;
  if (params) {
    const qs = new URLSearchParams(params).toString();
    if (qs) url += `?${qs}`;
  }

  const init = { method, headers };
  if (body !== null && method !== 'GET') init.body = JSON.stringify(body);

  let res;
  try {
    res = await fetch(url, init);
  } catch (err) {
    toast.error('Network error — check your connection');
    const e = new Error('Network error');
    e.cause = err;
    throw e;
  }

  if (res.status === 401) {
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
    const err = new Error('Unauthorized');
    err.response = { status: 401 };
    throw err;
  }

  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }

  if (!res.ok) {
    const message = data?.error || data?.message || `Request failed (${res.status})`;
    toast.error(message);
    const err = new Error(message);
    err.response = { status: res.status, data };
    throw err;
  }

  return data;
}

export function apiGet(path, params) {
  return request(path, { method: 'GET', params });
}

export function apiPost(path, body) {
  return request(path, { method: 'POST', body });
}

export function apiPut(path, body) {
  return request(path, { method: 'PUT', body });
}

export function apiDelete(path) {
  return request(path, { method: 'DELETE' });
}

// Silent variant — no toasts, no throws. For background sync / on-launch operations.
export async function silentFetch(path, method = 'GET', body = null) {
  try {
    const authHeaders = await getAuthHeaders();
    const init = {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeaders },
    };
    if (body) init.body = JSON.stringify(body);
    const res = await fetch(`${API_ENDPOINT}${path}`, init);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function normalizeNumber(input) {
  if (!input) return '';
  if (input.startsWith('+')) return '+' + input.slice(1).replace(/\D/g, '');
  return '+' + input.replace(/\D/g, '');
}

export function formatNumber(e164) {
  if (!e164?.startsWith('+')) return e164;
  const digits = e164.slice(1);
  if (digits.startsWith('44') && digits.length >= 12) return `+44 ${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  if (digits.startsWith('1') && digits.length === 11) return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  if (digits.startsWith('61') && digits.length >= 11) return `+61 ${digits.slice(2, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
  if (digits.startsWith('91') && digits.length === 12) return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  if (digits.startsWith('49') && digits.length >= 12) return `+49 ${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8)}`;
  if (digits.startsWith('55') && digits.length >= 12) return `+55 ${digits.slice(2, 4)} ${digits.slice(4, 9)} ${digits.slice(9)}`;
  if (digits.startsWith('27') && digits.length >= 11) return `+27 ${digits.slice(2, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  if (digits.startsWith('81') && digits.length >= 11) return `+81 ${digits.slice(2, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
  return e164;
}

export async function lookupNumber(rawNumber) {
  const normalized = normalizeNumber(rawNumber);
  return apiGet(`/lookup/${encodeURIComponent(normalized)}`);
}

export async function submitReport(number, category, comment) {
  return apiPost('/report', { number, category, comment });
}

export async function getTrending() {
  return apiGet('/trending');
}

export async function getStats() {
  return apiGet('/stats');
}

export async function getBlocklist() {
  return apiGet('/blocklist');
}

export async function addToBlocklist(number, formatted, label) {
  return apiPost('/blocklist', { number, formatted, label });
}

export async function removeFromBlocklist(number) {
  return apiDelete(`/blocklist/${encodeURIComponent(number)}`);
}

export async function deleteAccount() {
  return apiDelete('/user/account');
}
